import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import type { PlatformInstitutionRecord } from '@/types/platform';
import styles from './InstitutionPicker.module.css';

interface InstitutionPickerProps {
  id: string;
  value: string;
  onChange: (institutionId: string) => void;
  /** Adds a leading "All institutions" option that clears the selection. */
  clearable?: boolean;
  placeholder?: string;
}

/**
 * Pick an institution by NAME, not by pasting its UUID.
 *
 * Half the platform console used to open with a bare text box expecting an
 * institution id — an operator had to fetch the UUID from another screen and
 * paste it in before the page would show anything. This loads the register
 * once (one cache key shared by every page that mounts a picker) and offers a
 * searchable dropdown of names, with the code and id visible so the operator
 * can confirm they picked the right school.
 *
 * If the directory cannot be loaded the picker degrades to the old raw-id
 * input rather than dead-ending the page — worse UX beats no page.
 */
/** The institution's name where only its id is held — same cached register. */
export function InstitutionName({ institutionId }: { institutionId: string }) {
  const fetcher = useCallback(() => platformApi.institutions.list(), []);
  const { state } = useAsyncData<PlatformInstitutionRecord[]>(
    'platform-institution-picker',
    fetcher,
    true,
  );
  const match =
    state.status === 'ready'
      ? state.data.find((institution) => institution.id === institutionId)
      : undefined;
  return <>{match?.name ?? institutionId}</>;
}

export function InstitutionPicker({
  id,
  value,
  onChange,
  clearable = false,
  placeholder,
}: InstitutionPickerProps) {
  const { t } = useTranslation();

  const fetcher = useCallback(() => platformApi.institutions.list(), []);
  const { state } = useAsyncData<PlatformInstitutionRecord[]>(
    'platform-institution-picker',
    fetcher,
    true,
  );

  if (state.status === 'error') {
    return (
      <input
        id={id}
        className={styles.fallbackInput}
        value={value}
        placeholder={t('platform.picker.fallbackPlaceholder')}
        title={t('platform.picker.fallbackHint')}
        onChange={(event) => onChange(event.target.value.trim())}
      />
    );
  }

  const institutions = state.status === 'ready' ? state.data : [];
  const options = [
    ...(clearable ? [{ value: '', label: t('platform.picker.all') }] : []),
    ...institutions.map((institution) => ({
      value: institution.id,
      label: institution.name,
      description: institution.code,
    })),
  ];

  // A selected institution the register does not (yet) list — a stale URL, a
  // just-created school — stays selectable instead of silently vanishing.
  if (value && !institutions.some((institution) => institution.id === value)) {
    options.push({ value, label: value, description: t('platform.picker.unknownInstitution') });
  }

  return (
    <Select
      id={id}
      searchable
      value={value}
      onChange={onChange}
      placeholder={
        state.status === 'loading'
          ? t('platform.picker.loading')
          : (placeholder ?? t('platform.picker.placeholder'))
      }
      options={options}
    />
  );
}
